import { IconeAlerta, IconeConcluido, IconeRisco } from './icones'
import {
  EQUIPAMENTOS_ESTABILIZACAO,
  IDS_EQUIPAMENTO_ESTABILIZACAO,
  IDS_POSICAO_ESTABILIZACAO,
  POSICOES_ESTABILIZACAO,
  cenarioLiberado,
  instrucaoCliqueDaPosicao,
  zonasDaEstabilizacao,
  type AvaliacaoEstabilizacao,
  type ColocacaoEquipamento,
  type EquipamentoEstabilizacao,
  type PosicaoEstabilizacao,
} from '../dominio/didatico/estabilizacao'
import type { PerfilVeiculo } from '../dominio/didatico/perfilVeiculo'
import {
  calcularSobrecargaCervicalSuspensao,
  ESTADO_SUSPENSAO_INICIAL,
  type EstadoSuspensao,
  type ForcaPerturbacao,
} from '../dominio/didatico/suspensaoVeiculo'

export type SecaoPainelEstabilizacao =
  | 'cenario'
  | 'equipamentos'
  | 'suspensao'
  | 'visualizacao'
  | 'avaliacao'

interface Props {
  perfil: PerfilVeiculo
  posicao: PosicaoEstabilizacao
  equipamentoSelecionado: EquipamentoEstabilizacao
  colocacoes: readonly ColocacaoEquipamento[]
  avaliacao: AvaliacaoEstabilizacao
  concluidos: readonly PosicaoEstabilizacao[]
  mostrarInstabilidade: boolean
  estadoSuspensao?: EstadoSuspensao
  colarCervical?: boolean
  aoEscolherPosicao: (posicao: PosicaoEstabilizacao) => void
  aoEscolherEquipamento: (equipamento: EquipamentoEstabilizacao) => void
  aoAlternarInstabilidade: (valor: boolean) => void
  aoAlternarColarCervical?: (valor: boolean) => void
  aoPerturbar?: (forca: ForcaPerturbacao) => void
  aoReiniciar: () => void
  /** Permite compor o painel em bandejas independentes do ribbon. */
  secoes?: readonly SecaoPainelEstabilizacao[]
}

export function PainelEstabilizacao(props: Props) {
  const definicao = POSICOES_ESTABILIZACAO[props.posicao]
  const zonas = zonasDaEstabilizacao(props.posicao, props.perfil).filter((z) => z.obrigatoria)
  const zonasObrigatoriasCobertas = zonas.filter((zona) => props.avaliacao.zonasCobertas.includes(zona.id)).length
  const ultima = props.avaliacao.leituras.at(-1)
  const indiceAtual = IDS_POSICAO_ESTABILIZACAO.indexOf(props.posicao)
  const proxima = IDS_POSICAO_ESTABILIZACAO[indiceAtual + 1]

  const suspensao = props.estadoSuspensao ?? ESTADO_SUSPENSAO_INICIAL
  const colar = props.colarCervical ?? false
  const diagnosticoCervical = calcularSobrecargaCervicalSuspensao(suspensao, colar)
  const quantidadeApoiosLocais = props.avaliacao.rigidezProgressiva.apoiosLocais?.length ?? 0
  const secoes = new Set<SecaoPainelEstabilizacao>(
    props.secoes ?? ['cenario', 'equipamentos', 'suspensao', 'visualizacao', 'avaliacao'],
  )

  return (
    <>
      {secoes.has('cenario') && <div className="grupo-controle">
        <span className="titulo-controle">Cenários de estabilização</span>
        <div className="trilha-cenarios">
          {IDS_POSICAO_ESTABILIZACAO.map((id) => {
            const concluido = props.concluidos.includes(id)
            return (
              <button
                key={id}
                className={`${props.posicao === id ? 'ativo' : ''} ${concluido ? 'concluido' : ''}`}
                onClick={() => props.aoEscolherPosicao(id)}
                title={POSICOES_ESTABILIZACAO[id].objetivo}
              >
                <span>{concluido ? <IconeConcluido /> : POSICOES_ESTABILIZACAO[id].nivel}</span>
                {POSICOES_ESTABILIZACAO[id].nome.replace(/^\d+\. /, '')}
              </button>
            )
          })}
        </div>
        <p className="descricao-fase">{definicao.objetivo}</p>
      </div>}

      {secoes.has('equipamentos') && <div className="grupo-controle instrucao-posicionamento">
        <span className="titulo-controle">1. Escolha o equipamento</span>
        <div className="lista-equipamentos">
          {IDS_EQUIPAMENTO_ESTABILIZACAO.map((id) => {
            const equipamento = EQUIPAMENTOS_ESTABILIZACAO[id]
            const quantidade = props.colocacoes.filter((c) => c.equipamento === id).length
            return (
              <button
                key={id}
                className={props.equipamentoSelecionado === id ? 'ativo' : ''}
                onClick={() => props.aoEscolherEquipamento(id)}
              >
                <strong>{equipamento.nome}{quantidade > 0 ? ` × ${quantidade}` : ''}</strong>
                <small>{equipamento.funcao}</small>
              </button>
            )
          })}
        </div>
        <p className="instrucao-clique">
          <strong>2. Clique no solo ou vazio da maquete</strong>
          {instrucaoCliqueDaPosicao(props.posicao)}
        </p>
      </div>}

      {/* Seção da Física da Estabilização e Perturbação Dinâmica */}
      {secoes.has('suspensao') && (
        <div
          style={{
            padding: '12px',
            borderRadius: '8px',
            backgroundColor: '#1e293b',
            border: '1px solid #334155',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          {props.posicao === 'quatroRodas' ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#f8fafc', textTransform: 'uppercase' }}>
                  Física da Suspensão & Vítima
                </span>
                <span
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '999px',
                    backgroundColor: props.avaliacao.suspensaoAnulada ? '#059669' : '#dc2626',
                    color: '#ffffff',
                  }}
                >
                  {props.avaliacao.suspensaoAnulada ? 'MOLAS ANULADAS' : 'MOLAS ATIVAS'}
                </span>
              </div>

              {/* Teste de perturbação / bombeiros movimentando */}
              <button
                type="button"
                onClick={() =>
                  props.aoPerturbar?.({
                    forcaY: -16,
                    torqueRoll: Math.random() > 0.5 ? 12 : -12,
                    torquePitch: 10,
                    forcaZ: 4,
                  })
                }
                disabled={props.avaliacao.suspensaoAnulada && props.avaliacao.rolamentoTravado}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: props.avaliacao.suspensaoAnulada && props.avaliacao.rolamentoTravado ? '#334155' : '#3b82f6',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  border: 'none',
                  cursor: props.avaliacao.suspensaoAnulada && props.avaliacao.rolamentoTravado ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <IconeRisco /> Simular Ação de Bombeiros (Perturbar)
              </button>

              {/* Toggle de Colar Cervical */}
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.82rem', color: '#e2e8f0' }}>
                <input
                  type="checkbox"
                  checked={colar}
                  onChange={(e) => props.aoAlternarColarCervical?.(e.target.checked)}
                />
                <span>Colar cervical aplicado na vítima</span>
              </label>

              {/* Medidor de sobrecarga cervical */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                  <span style={{ color: '#94a3b8' }}>Sobrecarga Cervical:</span>
                  <strong
                    style={{
                      color:
                        diagnosticoCervical.nivel === 'estavel'
                          ? '#10b981'
                          : diagnosticoCervical.nivel === 'atencao'
                            ? '#f59e0b'
                            : '#ef4444',
                    }}
                  >
                    {diagnosticoCervical.sobrecargaPct}%
                  </strong>
                </div>
                <div style={{ height: '6px', backgroundColor: '#0f172a', borderRadius: '3px', overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${diagnosticoCervical.sobrecargaPct}%`,
                      height: '100%',
                      backgroundColor:
                        diagnosticoCervical.nivel === 'estavel'
                          ? '#10b981'
                          : diagnosticoCervical.nivel === 'atencao'
                            ? '#f59e0b'
                            : '#ef4444',
                      transition: 'width 0.15s ease-out',
                    }}
                  />
                </div>
                <p style={{ fontSize: '0.72rem', color: '#cbd5e1', margin: '2px 0 0', lineHeight: 1.3 }}>
                  {diagnosticoCervical.leitura}
                </p>
              </div>

              {/* Indicadores de rigidez progressiva por grau de liberdade */}
              <div style={{ background: '#0f172a', padding: '8px', borderRadius: '6px', fontSize: '0.75rem', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>Apoios locais independentes:</span>
                  <strong style={{ color: quantidadeApoiosLocais > 0 ? '#10b981' : '#f59e0b' }}>
                    {quantidadeApoiosLocais}
                  </strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>Rigidez Vertical (Calçamento):</span>
                  <strong style={{ color: '#38bdf8' }}>{Math.round((props.avaliacao.rigidezProgressiva?.vertical ?? 0) * 100)}%</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>Rigidez Roll (Lateral):</span>
                  <strong style={{ color: '#38bdf8' }}>{Math.round((props.avaliacao.rigidezProgressiva?.roll ?? 0) * 100)}%</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>Rigidez Pitch (Arfagem):</span>
                  <strong style={{ color: '#38bdf8' }}>{Math.round((props.avaliacao.rigidezProgressiva?.pitch ?? 0) * 100)}%</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>Travamento Rodas (Diagonal):</span>
                  <strong style={{ color: props.avaliacao.rolamentoTravado ? '#10b981' : '#f59e0b' }}>
                    {props.avaliacao.rolamentoTravado ? '100% (2 Rodas Diagonais)' : `${Math.round((props.avaliacao.rigidezProgressiva?.longitudinal ?? 0) * 100)}% (Incompleto)`}
                  </strong>
                </div>
                <p style={{ margin: '4px 0 0', fontSize: '0.7rem', color: '#94a3b8', fontStyle: 'italic' }}>
                  Bloqueie 2 rodas diagonalmente opostas (2 cunhas por roda). Cada calço reduz progressivamente o movimento.
                </p>
              </div>
            </>
          ) : props.posicao === 'tombado' ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#f8fafc', textTransform: 'uppercase' }}>
                  Estabilidade Lateral & Escoramento
                </span>
                <span
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '999px',
                    backgroundColor: props.avaliacao.primariaConcluida ? '#059669' : '#dc2626',
                    color: '#ffffff',
                  }}
                >
                  {props.avaliacao.primariaConcluida ? 'ESTABILIZADO' : 'RISCO DE TOMBAMENTO'}
                </span>
              </div>

              <button
                type="button"
                onClick={() =>
                  props.aoPerturbar?.({
                    forcaY: -8,
                    torqueRoll: 16 * (1 - (props.avaliacao.rigidezProgressiva?.roll ?? 0)),
                    torquePitch: 6,
                  })
                }
                disabled={props.avaliacao.primariaConcluida}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: props.avaliacao.primariaConcluida ? '#334155' : '#3b82f6',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  border: 'none',
                  cursor: props.avaliacao.primariaConcluida ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <IconeRisco /> Simular Força Lateral (Perturbar)
              </button>

              <div style={{ background: '#0f172a', padding: '8px', borderRadius: '6px', fontSize: '0.75rem', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>Rigidez ao Tombamento (Roll):</span>
                  <strong style={{ color: (props.avaliacao.rigidezProgressiva?.roll ?? 0) >= 1 ? '#10b981' : '#38bdf8' }}>
                    {Math.round((props.avaliacao.rigidezProgressiva?.roll ?? 0) * 100)}%
                  </strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>V-Struts na Estrutura (Lata):</span>
                  <strong style={{ color: props.avaliacao.zonasCobertas.includes('escora-dianteira') && props.avaliacao.zonasCobertas.includes('escora-traseira') ? '#10b981' : '#f59e0b' }}>
                    {props.avaliacao.zonasCobertas.filter((z) => z.startsWith('escora-')).length} de 2 instalados
                  </strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>Calços Colunas A e C:</span>
                  <strong style={{ color: props.avaliacao.zonasCobertas.includes('base-dianteira') && props.avaliacao.zonasCobertas.includes('base-traseira') ? '#10b981' : '#f59e0b' }}>
                    {props.avaliacao.zonasCobertas.filter((z) => z.startsWith('base-')).length} de 2 instalados
                  </strong>
                </div>
                <p style={{ margin: '4px 0 0', fontSize: '0.7rem', color: '#94a3b8', fontStyle: 'italic' }}>
                  V-Struts ancoram na lata (próximo ao vidro da coluna C) sem fixar nas rodas; calços escalonados apoiam as colunas A e C no solo.
                </p>
              </div>
            </>
          ) : props.posicao === 'capotado' ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#f8fafc', textTransform: 'uppercase' }}>
                  Estabilidade Longitudinal & Gangorra
                </span>
                <span
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '999px',
                    backgroundColor: props.avaliacao.primariaConcluida ? '#059669' : '#dc2626',
                    color: '#ffffff',
                  }}
                >
                  {props.avaliacao.primariaConcluida ? 'SEM GANGORRA' : 'GANGORRA ATIVA'}
                </span>
              </div>

              <button
                type="button"
                onClick={() =>
                  props.aoPerturbar?.({
                    forcaY: -8,
                    torquePitch: 16 * (1 - (props.avaliacao.rigidezProgressiva?.pitch ?? 0)),
                    torqueRoll: 6,
                  })
                }
                disabled={props.avaliacao.primariaConcluida}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: props.avaliacao.primariaConcluida ? '#334155' : '#3b82f6',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  border: 'none',
                  cursor: props.avaliacao.primariaConcluida ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <IconeRisco /> Simular Gangorra (Perturbar)
              </button>

              <div style={{ background: '#0f172a', padding: '8px', borderRadius: '6px', fontSize: '0.75rem', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>Rigidez Longitudinal (Pitch):</span>
                  <strong style={{ color: (props.avaliacao.rigidezProgressiva?.pitch ?? 0) >= 1 ? '#10b981' : '#38bdf8' }}>
                    {Math.round((props.avaliacao.rigidezProgressiva?.pitch ?? 0) * 100)}%
                  </strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>Estrutura Motor / Coluna A (Bloco):</span>
                  <strong style={{ color: props.avaliacao.zonasCobertas.includes('capo-esquerdo') && props.avaliacao.zonasCobertas.includes('capo-direito') ? '#10b981' : '#f59e0b' }}>
                    {props.avaliacao.zonasCobertas.filter((z) => z.startsWith('capo-')).length} de 2 apoiados
                  </strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>Calços sob o Teto (Colunas B/C):</span>
                  <strong style={{ color: props.avaliacao.zonasCobertas.includes('traseira-esquerda') && props.avaliacao.zonasCobertas.includes('traseira-direita') ? '#10b981' : '#f59e0b' }}>
                    {props.avaliacao.zonasCobertas.filter((z) => z.startsWith('traseira-')).length} de 2 calçados
                  </strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>V-Struts na Lata (Coluna C):</span>
                  <strong style={{ color: props.avaliacao.zonasCobertas.includes('escora-capotado-esquerda') && (props.avaliacao.zonasCobertas.includes('escora-capotado-direita') || props.avaliacao.zonasCobertas.includes('escora-capotado')) ? '#10b981' : '#f59e0b' }}>
                    {props.avaliacao.zonasCobertas.filter((z) => z.startsWith('escora-capotado')).length} de 2 instalados
                  </strong>
                </div>
                <p style={{ margin: '4px 0 0', fontSize: '0.7rem', color: '#94a3b8', fontStyle: 'italic' }}>
                  Bloco entre motor e coluna A; calços sob o teto; V-Struts cravados na lata próxima ao vidro da coluna C (sem apoiar na roda).
                </p>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#f8fafc', textTransform: 'uppercase' }}>
                  Conjunto Monobloco
                </span>
                <span
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '999px',
                    backgroundColor: props.avaliacao.secundariaConcluida ? '#059669' : '#dc2626',
                    color: '#ffffff',
                  }}
                >
                  {props.avaliacao.secundariaConcluida ? 'UNIFICADO' : 'RISCO DE DESLIZAMENTO'}
                </span>
              </div>

              <button
                type="button"
                onClick={() =>
                  props.aoPerturbar?.({
                    forcaZ: 8 * (1 - (props.avaliacao.rigidezProgressiva?.pitch ?? 0)),
                    torqueRoll: 10 * (1 - (props.avaliacao.rigidezProgressiva?.roll ?? 0)),
                  })
                }
                disabled={props.avaliacao.secundariaConcluida}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: props.avaliacao.secundariaConcluida ? '#334155' : '#3b82f6',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  border: 'none',
                  cursor: props.avaliacao.secundariaConcluida ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <IconeRisco /> Simular Movimento Relativo (Perturbar)
              </button>

              <div style={{ background: '#0f172a', padding: '8px', borderRadius: '6px', fontSize: '0.75rem', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                  <span>Unificação Monobloco:</span>
                  <strong style={{ color: props.avaliacao.secundariaConcluida ? '#10b981' : '#38bdf8' }}>
                    {Math.round(props.avaliacao.progresso * 100)}%
                  </strong>
                </div>
                <p style={{ margin: '4px 0 0', fontSize: '0.7rem', color: '#94a3b8', fontStyle: 'italic' }}>
                  Calce o veículo inferior, preencha os vazios intermediários e trave o monobloco com fitas catraca.
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {secoes.has('visualizacao') && <label className="toggle toggle-instabilidade">
        <input
          type="checkbox"
          checked={props.mostrarInstabilidade}
          onChange={(e) => props.aoAlternarInstabilidade(e.target.checked)}
        />
        mostrar direção provável da instabilidade
      </label>}

      {secoes.has('avaliacao') && ultima && (
        <p className={`feedback-colocacao ${ultima.estado}`}>
          <strong>{ultima.estado === 'correta' ? 'Posição funcional' : ultima.estado === 'proxima' ? 'Quase lá' : 'Revise a posição'}</strong>
          {ultima.mensagem}
        </p>
      )}

      {secoes.has('suspensao') && props.posicao === 'quatroRodas' && (
        <div className={`status-suspensao ${props.avaliacao.suspensaoAnulada ? 'anulada' : 'ativa'}`}>
          <div className="cabecalho-suspensao">
            <span className="rotulo-suspensao">Doutrina CBMMG • Suspensão</span>
            <span className={`badge-suspensao ${props.avaliacao.suspensaoAnulada ? 'badge-anulada' : 'badge-ativa'}`}>
              {props.avaliacao.suspensaoAnulada
                ? <><IconeConcluido />Anulada (Rígida)</>
                : <><IconeAlerta />Ativa (Balanço residual)</>}
            </span>
          </div>
          <p className="motivo-suspensao">{props.avaliacao.motivoSuspensao}</p>
        </div>
      )}

      {secoes.has('avaliacao') && <div className={`avaliacao-estabilizacao ${props.avaliacao.secundariaConcluida ? 'concluida' : props.avaliacao.primariaConcluida ? 'andamento' : ''}`}>
        <div className="cabecalho-avaliacao">
          <strong>{Math.round(props.avaliacao.progresso * 100)}% do cenário</strong>
          <span>{zonasObrigatoriasCobertas}/{zonas.length} pontos úteis</span>
        </div>
        <div className="barra-progresso"><i style={{ width: `${props.avaliacao.progresso * 100}%` }} /></div>
        <p>{props.avaliacao.mensagem}</p>
        <div className="acoes-fase">
          {proxima && props.avaliacao.secundariaConcluida && (
            <button className="botao-proxima-fase" onClick={() => props.aoEscolherPosicao(proxima)}>
              Avançar para {POSICOES_ESTABILIZACAO[proxima].nome} →
            </button>
          )}
          <button className="botao-secundario" onClick={props.aoReiniciar}>
            Reiniciar posicionamentos
          </button>
        </div>
      </div>}
    </>
  )
}
